import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@radix-ui/react-tooltip";
import { Moon, Sun } from "lucide-react";
import Particles from "react-tsparticles";
import LogoImg from "../assets/logo.png";
import toast from "react-hot-toast";
import { ScrollToTop } from "../utils/scrollToTop";
import { Facebook, Instagram } from "react-feather";

const MainLayout = ({ children, hideNavbar = false }) => {
  const [darkMode, setDarkMode] = useState(false);
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode((prev) => !prev);
    toast.success(
      darkMode ? "Switched to Light Mode" : "Switched to Dark Mode"
    );
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-white via-[#f5f8ff] to-[#e4ecff] dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 text-black dark:text-white transition-colors duration-500">
      {/* Particles Background */}
      <ScrollToTop />
      <Particles
        id="tsparticles"
        options={{
          background: { color: { value: "transparent" } },
          fullScreen: { enable: false },
          particles: {
            color: { value: "#999" },
            links: {
              enable: true,
              distance: 130,
              color: "#ccc",
              opacity: 0.3,
            },
            move: { enable: true, speed: 1 },
            number: { value: 40 },
            opacity: { value: 0.3 },
            size: { value: 1.5 },
          },
        }}
        className="absolute inset-0 z-0"
      />

      {/* Navbar */}
      {!hideNavbar && (
        <motion.nav
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
          className="fixed top-0 left-0 w-full z-50 px-6 py-4 flex items-center justify-between backdrop-blur-md border-b border-white/30 dark:border-gray-800 bg-white/30 dark:bg-black/20 rounded-b-xl shadow-sm"
        >
          <div className="text-2xl font-extrabold tracking-widest text-gray-900 dark:text-white">
            <Link to="/" className="flex flex-col items-center ">
              <img src={LogoImg} alt="muhaad logo" className="h-6 rounded-md" />
              <span className="text-[10px]">MUHAAD</span>
              <span className="text-xs">TOUR & TRAVEL</span>
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex flex-1 justify-center items-center">
            <div className="flex space-x-6 text-sm md:text-base font-medium text-gray-800 dark:text-gray-200">
              <Link to="/" className="hover:text-teal-500 transition-colors">
                Home
              </Link>
              <Link
                to="/about-us"
                className="hover:text-teal-500 transition-colors"
              >
                About Us
              </Link>
              <Link
                to="/services"
                className="hover:text-teal-500 transition-colors"
              >
                Services
              </Link>
              <Link
                to="/drone"
                className="hover:text-teal-500 transition-colors"
              >
                Drone
              </Link>
              <Link
                to="/blog"
                className="hover:text-teal-500 transition-colors"
              >
                Blog
              </Link>
              <Link
                to="/gallery"
                className="hover:text-teal-500 transition-colors"
              >
                Gallery
              </Link>
              <Link
                to="/contact"
                className="hover:text-teal-500 transition-colors"
              >
                Contact
              </Link>
            </div>
          </div>

          {/* Dark Mode & Mobile Toggle */}
          <div className="flex items-center space-x-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={toggleDarkMode}
                    className="p-2 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition"
                  >
                    {darkMode ? <Sun size={18} /> : <Moon size={18} />}
                  </button>
                </TooltipTrigger>
                <TooltipContent className="bg-black text-white px-2 py-1 rounded text-xs">
                  Toggle {darkMode ? "Light" : "Dark"} Mode
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Hamburger Button for Mobile */}
            <button
              onClick={() => setShowMobileMenu((prev) => !prev)}
              className="lg:hidden p-2 rounded-md border border-white/30 bg-white/20 dark:bg-white/10 backdrop-blur-md"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-black dark:text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </motion.nav>
      )}
      {/* Mobile Nav */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-20 left-4 right-4 z-40 rounded-xl shadow-lg bg-white/40 dark:bg-black/40 backdrop-blur-md p-6 flex flex-col space-y-4 text-base text-gray-800 dark:text-gray-200 lg:hidden"
          >
            <Link
              to="/"
              onClick={() => setShowMobileMenu(false)}
              className="hover:text-teal-500"
            >
              Home
            </Link>
            <Link
              to="/about-us"
              onClick={() => setShowMobileMenu(false)}
              className="hover:text-teal-500"
            >
              About Us
            </Link>
            <Link
              to="/services"
              onClick={() => setShowMobileMenu(false)}
              className="hover:text-teal-500"
            >
              Services
            </Link>
            <Link
              to="/drone"
              onClick={() => setShowMobileMenu(false)}
              className="hover:text-teal-500"
            >
              Drone
            </Link>
            <Link
              to="/blog"
              onClick={() => setShowMobileMenu(false)}
              className="hover:text-teal-500"
            >
              Blog
            </Link>
            <Link
              to="/contact"
              onClick={() => setShowMobileMenu(false)}
              className="hover:text-teal-500"
            >
              Contact
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Page Content */}
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
          className="pt-20 px-4 sm:px-6 relative z-10"
        >
          {children}
        </motion.main>
      </AnimatePresence>

      {/* Footer */}
      <footer className="relative z-10 mt-20 px-6 py-12 backdrop-blur-md bg-white/30 dark:bg-black/30 rounded-t-xl text-gray-800 dark:text-gray-200 shadow-inner">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Logo & About */}
          <div>
            <div className="text-2xl font-bold mb-4">
              <Link to="/" className="flex flex-col items-center ">
                <img src={LogoImg} alt="muhaad logo" className="h-12" />
                <span className="text-xs">MUHAAD TOUR & TRAVEL</span>
              </Link>
            </div>
            <p className="text-sm leading-relaxed">
              Muhaad offers curated Ugandan travel experiences rooted in
              culture, nature, and luxury. Discover the unseen, connect with the
              authentic.
            </p>
          </div>

          {/* Useful Links */}
          <div>
            <h4 className="font-semibold mb-4">Useful Links</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>
                <Link to="/about-us">About Us</Link>
              </li>
              <li>
                <Link to="/services">Services</Link>
              </li>
              <li>
                <Link to="/drone">Drone</Link>
              </li>
              <li>
                <Link to="/blog">Blog</Link>
              </li>
              <li>
                <Link to="/gallery">Gallery</Link>
              </li>
              <li>
                <Link to="/contact">Contact</Link>
              </li>
              <li>
                <Link to="/faq">FAQ</Link>
              </li>
            </ul>
          </div>

          {/* Top Destinations */}
          <div>
            <h4 className="font-semibold mb-4">Top Destinations</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href="https://www.bwindiforestnationalpark.com/"
                  target="_blank"
                  className="hover:underline"
                  rel="noreferrer"
                >
                  Gorilla Trekking in Bwindi
                </a>
              </li>
              <li>
                <a
                  href="https://www.murchisonfallsnationalpark.com/"
                  target="_blank"
                  className="hover:underline"
                  rel="noreferrer"
                >
                  Murchison Falls Wildlife Safari
                </a>
              </li>
              <li>
                <a
                  href="https://sourceofthenileug.com/"
                  target="_blank"
                  className="hover:underline"
                  rel="noreferrer"
                >
                  Source of the Nile Adventure
                </a>
              </li>
              <li>
                <a
                  href="https://www.lakebunyonyiuganda.com/"
                  target="_blank"
                  className="hover:underline"
                  rel="noreferrer"
                >
                  Lake Bunyonyi Romantic Escape
                </a>
              </li>
            </ul>
          </div>

          {/* Instagram Feed Placeholder */}
          <div>
            <h4 className="font-semibold mb-4">Follow us on</h4>
            <div className="flex flex-row items-center space-x-2 gap-2">
              <a
                href="https://www.facebook.com/share/1HH18WBvmY/?mibextid=wwXIfr"
                target="_blank"
                rel="noreferrer"
                className="hover:text-teal-500 "
              >
                <Facebook />
              </a>
              <a
                href=" https://www.instagram.com/muhaadtravel"
                rel="noreferrer"
                className="hover:text-teal-500 "
              >
                <Instagram />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/20 mt-10 pt-4 flex flex-col md:flex-row justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>
            © {new Date().getFullYear()} MUHAAD Tours. All rights reserved. |
            Developed by{" "}
            <a
              href="https://ashitacreatives.com"
              target="_blank"
              rel="noreferrer"
            >
              Ashita Creatives
            </a>
          </span>
          <div className="flex space-x-4 mt-2 md:mt-0">
            <a
              href="/privacy-policy"
              className="hover:text-black dark:hover:text-white"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLayout;
